import { ErrorHandler, LOCALE_ID, NgModule, inject, provideAppInitializer } from '@angular/core';
import {BrowserModule} from '@angular/platform-browser';
import {JWT_OPTIONS, JwtModule} from '@auth0/angular-jwt';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {StateService, UIRouterModule, UIView} from '@uirouter/angular';
import {TranslateModule} from '@ngx-translate/core';
import {provideTranslateHttpLoader} from '@ngx-translate/http-loader';
import {ToastrModule} from 'ngx-toastr';
import {provideAnimationsAsync} from '@angular/platform-browser/animations/async';
import {RECAPTCHA_V3_SITE_KEY} from 'ng-recaptcha-2';
import {environment} from '../environments/environment';
import {routerConfigFn, STATES} from './main.routes';
import {DatePipe, registerLocaleData} from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeDeExtra from '@angular/common/locales/extra/de';
import localeIt from '@angular/common/locales/it';
import localeItExtra from '@angular/common/locales/extra/it';
import {AuthPagesModule} from './modules/auth-pages.module';
import {ActivityTrackerPublisher} from './original-services/activity-tracker.service';
import {LazyActivityTrackerPublisher} from './services/activity-tracker/lazy-activity-tracker-publisher.service';
import {GlobalErrorHandler} from './common/global-error-handler';
import {AuthenticationInterceptor} from './common/authentication.interceptor';
import {ResponseInterceptor} from './common/response.interceptor';
import {SetupIntercomFactory} from './common/intercom.factory';
import {LocaleFactory} from './common/locale.factory';
import {IntercomService} from './services/intercom/intercom.service';
import {LoggerService} from './original-services/logger.service';
import {StorageService} from './original-services/storage.service';
import {BankAccountsService} from './original-services/bank-accounts.service';
import {CompanyService} from './original-services/company.service';
import {CompanyGroupService} from './original-services/company-group.service';
import {GroupService} from './original-services/group.service';
import {SetupTranslateFactory} from './common/translator.factory';
import {ColorThemeService} from './services/profile/color-theme.service';
import {AppVersionService} from './services/profile/app-version.service';
import {LanguageService} from './original-services/language.service';
import {FeatureFlagsService} from './services/feature-flags/feature-flags.service';
import { FeatureFlagRouteGuardService } from './services/feature-flags/feature-flag-route-guard.service';
import { MobileInsightsOnlyGuardService } from './services/mobile-insights-only-guard.service';


registerLocaleData(localeDe, localeDeExtra, 'de-DE');
registerLocaleData(localeIt, localeItExtra, 'it-IT');

export function jwtOptionsFactory() {
    return {
        tokenGetter: () => {
            return null;
        }
    }
}

export function initColorTheme(colorThemeService: ColorThemeService) {
    return () => colorThemeService.setupColorTheme();
}

export function initVersionCheck(versionCheckService: AppVersionService) {
    return () => versionCheckService.setupVersionCheck();
}

export function initFeatureFlags(featureFlagsService: FeatureFlagsService) {
    return () => featureFlagsService.initFeatureFlags();
}

export function initRouteGuards(_flagGuard: FeatureFlagRouteGuardService, _mobileGuard: MobileInsightsOnlyGuardService) {
    // No-op initializer to ensure both guard services are constructed and register their router hooks
    return () => true;
}

@NgModule({ declarations: [],
    // No bootstrap array needed for standalone bootstrap
    imports: [BrowserModule,
        JwtModule.forRoot({
            jwtOptionsProvider: {
                provide: JWT_OPTIONS,
                useFactory: jwtOptionsFactory
            }
        }),
        TranslateModule.forRoot(),
        UIRouterModule.forRoot({
            states: STATES,
            otherwise: '/',
            useHash: true,
            config: routerConfigFn
        }),
        // Only the PUBLIC pages are eager. Everything authenticated arrives via the `app.**`
        // future state in main.routes.ts, which lazily loads AppShellModule.
        AuthPagesModule,
        // DirectivesModule deliberately NOT imported here. It declares PdfViewerAutosizeDirective,
        // which pulls ng2-pdf-viewer/pdfjs (~1 MB+) into the eager bundle, and an import in AppModule
        // makes nothing visible to AuthPagesModule's templates anyway. Authenticated code gets it via
        // SharedModule/PartialComponentsModule; auth pages import the leaves they need directly.
        // Toastr's forRoot() must live here, in the root injector. It was previously in
        // PageComponentsModule, which is now lazily loaded — a forRoot() there would build a
        // second, separate singleton instead of configuring the root one.
        //
        // NgxDaterangepickerMd deliberately does NOT get hoisted here: no public/eager page uses a
        // date range picker, and PartialComponentsModule and WidgetComponentsModule already call its
        // forRoot() on the lazy side. Adding it here would pull the picker and its date library into
        // the eager bundle for no benefit.
        ToastrModule.forRoot({
            positionClass: 'toast-bottom-right'
        })
    ], providers: [
        DatePipe,
        // BrowserAnimationsModule used to be imported by PageComponentsModule, UIComponentsModule,
        // WidgetComponentsModule and PartialComponentsModule. It re-exports BrowserModule, so once
        // those modules moved behind the lazy boundary Angular threw NG05100 ("Providers from the
        // BrowserModule have already been loaded") on the first authenticated navigation. The
        // animation engine belongs to the root injector only; the async variant also keeps it off
        // the eager critical path.
        provideAnimationsAsync(),
        // App-wide providers, relocated here from PageComponentsModule as part of the Tier 2 lazy
        // split. All of these fail silently from a lazily loaded injector: interceptors never reach
        // the root HttpClient, APP_INITIALIZERs are collected at bootstrap and simply never run, and
        // LOCALE_ID/ErrorHandler are only read from the root injector.
        {
            provide: ErrorHandler,
            useClass: environment.production ? GlobalErrorHandler : ErrorHandler
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthenticationInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ResponseInterceptor,
            multi: true,
        },
        provideAppInitializer(() => {
            const initializerFn = (SetupIntercomFactory)(inject(IntercomService), inject(LoggerService), inject(StateService), inject(StorageService), inject(BankAccountsService), inject(CompanyService), inject(CompanyGroupService), inject(GroupService));
            return initializerFn();
        }),
        {
            provide: LOCALE_ID,
            deps: [LanguageService],
            useFactory: LocaleFactory
        },
        // Needed by the EAGER auth pages: sign-in, sign-up and restoring all inject ReCaptchaV3Service.
        { provide: RECAPTCHA_V3_SITE_KEY, useValue: environment.googleCaptchaKey },
        // Must resolve from the ROOT injector: ActivityTrackerService (providedIn: 'root', used by
        // eager services) injects it, and a provider in the lazy AppShellModule is invisible to it
        // (NG0201). The lazy proxy keeps the GetStream client itself out of the eager bundle.
        {
            provide: ActivityTrackerPublisher,
            useExisting: LazyActivityTrackerPublisher
        },
        provideAppInitializer(() => {
        const initializerFn = (SetupTranslateFactory)(inject(LanguageService));
        return initializerFn();
      }),
        provideAppInitializer(() => {
        const initializerFn = (initColorTheme)(inject(ColorThemeService));
        return initializerFn();
      }),
        provideAppInitializer(() => {
        const initializerFn = (initVersionCheck)(inject(AppVersionService));
        return initializerFn();
      }),
        provideAppInitializer(() => {
        const initializerFn = (initFeatureFlags)(inject(FeatureFlagsService));
        return initializerFn();
      }),
        provideAppInitializer(() => {
        const initializerFn = (initRouteGuards)(inject(FeatureFlagRouteGuardService), inject(MobileInsightsOnlyGuardService));
        initializerFn();
      }),
        provideHttpClient(withInterceptorsFromDi()),
        provideTranslateHttpLoader({
            prefix: './assets/i18n/',
            suffix: '.json?d=' + new Date().getTime()
        })
    ] })
export class AppModule {
}
